import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import reviewRulesHwp from '@/assets/files/논문심사 규정.hwp';
import submissionRulesHwp from '@/assets/files/논문투고 규정.hwp';
import englishRulesPdf from '@/assets/files/영문규정.pdf';
import editorialBoardRulesPdf from '@/assets/files/편집위원회 규정.pdf';
import ethicsRulesPdf from '@/assets/files/한국응용과학기술학회 연구윤리규정.pdf';

import * as S from './style.ts';

type RegulationTab = {
	description: string;
	key: string;
	title: string;
};

type RuleItem = {
	body: string;
	href?: string;
	linkLabel?: string;
	title: string;
};

type RegulationDocument = {
	fileName: string;
	url: string;
};

const regulationDocuments: Partial<Record<string, RegulationDocument>> = {
	submission: {
		fileName: '논문투고 규정.hwp',
		url: submissionRulesHwp,
	},
	review: {
		fileName: '논문심사 규정.hwp',
		url: reviewRulesHwp,
	},
	english: {
		fileName: '영문규정.pdf',
		url: englishRulesPdf,
	},
	ethics: {
		fileName: '한국응용과학기술학회 연구윤리규정.pdf',
		url: ethicsRulesPdf,
	},
	'editorial-board': {
		fileName: '편집위원회 규정.pdf',
		url: editorialBoardRulesPdf,
	},
};

const PublicationJournalGuide: React.FC = () => {
	const { t } = useTranslation();
	const regulationTabs = t('publication.journalGuide.tabs', { returnObjects: true }) as RegulationTab[];
	const [activeTab, setActiveTab] = useState(regulationTabs[0]?.key ?? 'submission');

	const currentTab = regulationTabs.find((tab) => tab.key === activeTab) ?? regulationTabs[0];
	const ruleGroups = useMemo<Record<string, RuleItem[]>>(
		() => ({
			submission: t('publication.journalGuide.submissionRules', { returnObjects: true }) as RuleItem[],
			review: t('publication.journalGuide.reviewRules', { returnObjects: true }) as RuleItem[],
			english: t('publication.journalGuide.englishRules', { returnObjects: true }) as RuleItem[],
			ethics: t('publication.journalGuide.ethicsRules', { returnObjects: true }) as RuleItem[],
			'ethics-committee': t('publication.journalGuide.ethicsCommitteeRules', { returnObjects: true }) as RuleItem[],
			rights: t('publication.journalGuide.rightsRules', { returnObjects: true }) as RuleItem[],
		}),
		[t]
	);

	const rules = currentTab ? ruleGroups[currentTab.key] : undefined;
	const currentDocument = currentTab ? regulationDocuments[currentTab.key] : undefined;

	return (
		<S.Container>
			<S.HeaderBox>
				<S.Eyebrow>{t('publication.journalGuide.header.eyebrow')}</S.Eyebrow>
				<S.Title>{t('publication.journalGuide.header.title')}</S.Title>
				<S.Description>{t('publication.journalGuide.header.description')}</S.Description>
			</S.HeaderBox>

			<S.RegulationTabWrap>
				{regulationTabs.map((tab) => (
					<S.RegulationTabButton key={tab.key} className={tab.key === activeTab ? 'active' : ''} onClick={() => setActiveTab(tab.key)}>
						{tab.title}
					</S.RegulationTabButton>
				))}
			</S.RegulationTabWrap>

			{rules ? (
				<S.RuleTextSection>
					{rules.map((rule) => (
						<S.RuleItem key={rule.title}>
							<S.RuleTitle>{rule.title}</S.RuleTitle>
							<S.RuleBody>
								{rule.href ? (
									<S.RuleLink href={rule.href} target="_blank" rel="noreferrer">
										{rule.linkLabel ?? rule.body}
									</S.RuleLink>
								) : (
									rule.body
								)}
							</S.RuleBody>
						</S.RuleItem>
					))}
				</S.RuleTextSection>
			) : null}

			<S.ContentPanel>
				<S.ContentHeader>
					<S.ContentTitle>{currentTab.title}</S.ContentTitle>
				</S.ContentHeader>

				<S.ContentDescription>{currentTab.description}</S.ContentDescription>
				{currentDocument ? (
					<S.DocumentPanel>
						<S.DocumentName>{currentDocument.fileName}</S.DocumentName>
						<S.DownloadButton type="button" onClick={() => window.open(currentDocument.url, '_blank', 'noopener,noreferrer')}>
							{t('publication.search.buttons.download')}
						</S.DownloadButton>
					</S.DocumentPanel>
				) : (
					<S.EmptyBox>{t('publication.journalGuide.empty')}</S.EmptyBox>
				)}
			</S.ContentPanel>
		</S.Container>
	);
};

export default PublicationJournalGuide;
